/**
 * Read-only admin activity log. Visible only to Full Admins with the grant.
 */

import React, { useEffect, useState } from 'react';
import garageAPI from '../../../services/garage/api';
import { formatApiError } from '../../../utils/formatApiError';

const ACTION_GROUPS = [
  {
    label: 'Tools & Agents',
    options: [
      { value: 'tool.created', label: 'Tool created' },
      { value: 'tool.updated', label: 'Tool updated' },
      { value: 'tool.published', label: 'Tool published' },
      { value: 'tool.unpublished', label: 'Tool unpublished' },
      { value: 'tool.deleted', label: 'Tool deleted' },
    ],
  },
  {
    label: 'Users & Admins',
    options: [
      { value: 'user.access_changed', label: 'User access changed' },
      { value: 'user.activity_access_changed', label: 'Activity access changed' },
      { value: 'user.created', label: 'User created' },
      { value: 'user.updated', label: 'User updated' },
      { value: 'user.deleted', label: 'User deleted' },
    ],
  },
  {
    label: 'Configuration',
    options: [
      { value: 'persona_webhook.saved', label: 'Persona webhook saved' },
      { value: 'persona_webhook.removed', label: 'Persona webhook removed' },
      { value: 'persona_webhook.test_requested', label: 'Persona webhook test requested' },
      { value: 'config.created', label: 'Configuration created' },
      { value: 'config.updated', label: 'Configuration updated' },
      { value: 'config.deactivated', label: 'Configuration deactivated' },
      { value: 'config.reactivated', label: 'Configuration reactivated' },
      { value: 'config.deleted', label: 'Configuration deleted' },
    ],
  },
];

const ACTION_OPTIONS = ACTION_GROUPS.flatMap((group) => group.options);

const RESOURCE_OPTIONS = [
  { value: '', label: 'All resources' },
  { value: 'tool', label: 'Tools' },
  { value: 'config', label: 'Configuration' },
  { value: 'persona_webhook', label: 'Persona webhooks' },
  { value: 'user', label: 'Users' },
];

const formatWhen = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const stateEntries = (state) => Object.entries(state || {});

const stateValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return null;
  return String(value);
};

const fieldLabel = (key) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, (letter) => letter.toUpperCase());

const renderStateValue = (value) => {
  const primitive = stateValue(value);
  if (primitive !== null) return primitive;
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    if (value.every((item) => item === null || ['string', 'number', 'boolean'].includes(typeof item))) {
      return value.map((item) => stateValue(item)).join(', ');
    }
    return (
      <ul className="garage-activity-nested-list">
        {value.map((item, index) => <li key={index}>{renderStateValue(item)}</li>)}
      </ul>
    );
  }
  const entries = Object.entries(value);
  if (entries.length === 0) return 'None';
  return (
    <span className="garage-activity-inline-object">
      {entries.map(([key, nestedValue], index) => (
        <React.Fragment key={key}>
          {index > 0 && <span className="garage-activity-value-separator"> · </span>}
          <span><strong>{fieldLabel(key)}:</strong> {renderStateValue(nestedValue)}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

const renderMakerAssignments = (makers) => {
  if (!makers.length) return 'None';
  return (
    <div className="garage-activity-maker-list">
      {makers.map((maker, index) => (
        <div className="garage-activity-maker" key={maker.email || index}>
          {[
            ['Role', maker.role],
            ['Email', maker.email],
            ['Is Primary', maker.isPrimary ? 'Yes' : 'No'],
          ].map(([label, value]) => (
            <div className="garage-activity-maker-detail" key={label}>
              <span>{label}</span>
              <span>{value || '—'}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const ChangeGroup = ({ title, state }) => {
  const entries = stateEntries(state);
  return (
    <div className="garage-activity-change-group">
      <div className="garage-activity-change-heading">{title}</div>
      {entries.length === 0 ? (
        <div className="garage-activity-change-empty">—</div>
      ) : (
        <dl className="garage-activity-change-fields">
          {entries.map(([key, value]) => (
            <div
              className={`garage-activity-change-field${key === 'makers' ? ' garage-activity-change-field-makers' : ''}`}
              key={key}
            >
              <dt>{fieldLabel(key)}</dt>
              <dd>{key === 'makers' && Array.isArray(value)
                ? renderMakerAssignments(value)
                : renderStateValue(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const GarageAdminActivityPanel = () => {
  const [filters, setFilters] = useState({
    actor: '',
    action: '',
    resource_type: '',
    from_date: '',
    to_date: '',
  });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const params = { limit: pageSize, offset: page * pageSize };
        Object.entries(applied).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const data = await garageAPI.getAdminActivity(params);
        if (!cancelled) {
          const total = data.total || 0;
          const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
          if (page > lastPage) {
            setPage(lastPage);
            return;
          }
          setResult({ total, items: data.items || [] });
        }
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, 'Could not load the activity log.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [applied, page, pageSize]);

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(0);
    setApplied(filters);
  };

  const clearFilters = () => {
    const empty = {
      actor: '',
      action: '',
      resource_type: '',
      from_date: '',
      to_date: '',
    };
    setFilters(empty);
    setApplied(empty);
    setPage(0);
  };

  const filtersActive = Object.values(applied).some(Boolean);

  const pageCount = Math.max(1, Math.ceil((result.total || 0) / pageSize));
  const visiblePages = [...new Set([0, pageCount - 1, page - 1, page, page + 1])]
    .filter((index) => index >= 0 && index < pageCount)
    .sort((a, b) => a - b);

  const actionLabel = (action) => (
    ACTION_OPTIONS.find((option) => option.value === action)?.label || action
  );

  return (
    <div className="garage-admin-section garage-activity-panel">
      <h3 style={{ marginTop: 0 }}>Admin activity</h3>
      <p style={{ color: 'var(--garage-text-2)', marginTop: 0 }}>
        Tool, user, and configuration changes. Rows are added automatically and cannot be edited.
      </p>
      <form onSubmit={applyFilters} className="garage-activity-filters">
        <div className="garage-activity-filter garage-activity-filter-actor">
          <label htmlFor="activity-actor">Actor email</label>
          <input
            id="activity-actor"
            type="search"
            placeholder="name@ringcentral.com"
            value={filters.actor}
            onChange={(event) => setFilters({ ...filters, actor: event.target.value })}
          />
        </div>
        <div className="garage-activity-filter">
          <label htmlFor="activity-action">Action</label>
          <select
            id="activity-action"
            value={filters.action}
            onChange={(event) => setFilters({ ...filters, action: event.target.value })}
          >
            <option value="">All actions</option>
            {ACTION_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="garage-activity-filter">
          <label htmlFor="activity-resource">Resource</label>
          <select
            id="activity-resource"
            value={filters.resource_type}
            onChange={(event) => setFilters({ ...filters, resource_type: event.target.value })}
          >
            {RESOURCE_OPTIONS.map((option) => (
              <option key={option.value || 'all-resources'} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="garage-activity-filter">
          <label htmlFor="activity-from">From</label>
          <input
            id="activity-from"
            type="date"
            value={filters.from_date}
            onChange={(event) => setFilters({ ...filters, from_date: event.target.value })}
          />
        </div>
        <div className="garage-activity-filter">
          <label htmlFor="activity-to">To</label>
          <input
            id="activity-to"
            type="date"
            value={filters.to_date}
            onChange={(event) => setFilters({ ...filters, to_date: event.target.value })}
          />
        </div>
        <div className="garage-activity-filter-actions">
          {filtersActive && (
            <button type="button" className="garage-btn garage-btn-secondary" onClick={clearFilters}>
              Clear
            </button>
          )}
          <button type="submit" className="garage-btn garage-btn-primary">Apply</button>
        </div>
      </form>

      {error && <p style={{ color: 'var(--garage-danger, #b42318)' }}>{error}</p>}
      {loading ? (
        <p>Loading activity…</p>
      ) : result.items.length === 0 ? (
        <p>No admin activity matches these filters.</p>
      ) : (
        <div className="garage-admin-table garage-activity-log">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>What changed</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((entry) => (
                <tr key={entry.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(entry.createdAt)}</td>
                  <td>
                    {entry.actorEmail}
                    {entry.actorRole ? <div style={{ color: 'var(--garage-text-3)' }}>{entry.actorRole}</div> : null}
                  </td>
                  <td>
                    <div>{entry.summary}</div>
                    <div style={{ color: 'var(--garage-text-3)', fontSize: '12px' }}>
                      {actionLabel(entry.action)}
                    </div>
                  </td>
                  <td>
                    <div className="garage-activity-change-groups">
                      <ChangeGroup title="Before" state={entry.previousState} />
                      <ChangeGroup title="After" state={entry.newState} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && (
        <nav className="garage-activity-pagination" aria-label="Activity pagination" aria-busy={loading}>
          <label className="garage-activity-page-size">
            Rows per page
            <select
              className="garage-admin-filter-select"
              value={pageSize}
              disabled={loading}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            >
              {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <span aria-live="polite">
            {loading ? 'Loading…' : `Showing ${result.total === 0 ? 0 : page * pageSize + 1}–${Math.min(page * pageSize + result.items.length, result.total)} of ${result.total}`}
          </span>
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            disabled={loading || page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Previous
          </button>
          <div className="garage-activity-page-numbers">
            {visiblePages.map((index, position) => (
              <React.Fragment key={index}>
                {position > 0 && index - visiblePages[position - 1] > 1 && <span aria-hidden="true">…</span>}
                <button
                  type="button"
                  className={`garage-btn ${index === page ? 'garage-btn-primary' : 'garage-btn-secondary'}`}
                  aria-label={`Page ${index + 1}`}
                  aria-current={index === page ? 'page' : undefined}
                  disabled={loading}
                  onClick={() => setPage(index)}
                >
                  {index + 1}
                </button>
              </React.Fragment>
            ))}
          </div>
          <span>Page {page + 1} of {pageCount}</span>
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            disabled={loading || page + 1 >= pageCount}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};

export default GarageAdminActivityPanel;
